import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CompareMetric, CompareWidgetData } from '../../models/loyalty-dashboard.model';
import { CompareMetricRowComponent } from './compare-metric-row.component';

function hasAnyTier(metric: CompareMetric): boolean {
  return metric.withLoyalty != null || metric.withoutLoyalty != null;
}

@Component({
  selector: 'app-how-you-compare-widget',
  standalone: true,
  imports: [CommonModule, CompareMetricRowComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="compare-card">
      <ng-container *ngIf="data(); else noData">
        <div class="compare-card__header">
          <h3 class="compare-card__title">How you compare</h3>
          <p class="compare-card__subtitle">Your numbers against similar businesses</p>
          <div class="compare-card__legend">
            <span class="compare-card__legend-item">
              <span class="compare-card__swatch compare-card__swatch--you"></span>
              You
            </span>
            <span class="compare-card__legend-item" *ngIf="hasWithLoyalty()">
              <span class="compare-card__swatch compare-card__swatch--with"></span>
              Similar businesses with loyalty
            </span>
            <span class="compare-card__legend-item" *ngIf="hasWithoutLoyalty()">
              <span class="compare-card__swatch compare-card__swatch--without"></span>
              Similar businesses without loyalty
            </span>
          </div>
        </div>

        <div class="compare-card__body">
          <ng-container *ngIf="visibleMetrics().length; else emptyMessage">
            <app-compare-metric-row *ngFor="let metric of visibleMetrics(); trackBy: trackByKey" [metric]="metric" />
          </ng-container>
          <ng-template #emptyMessage>
            <p class="compare-card__empty">Comparisons will appear once there are enough similar merchants.</p>
          </ng-template>
        </div>
      </ng-container>
      <ng-template #noData>
        <p class="compare-card__empty">No data available.</p>
      </ng-template>
    </div>
  `,
  styles: [
    `
      .compare-card {
        display: flex;
        flex-direction: column;
        gap: 18px;
        height: 100%;
        padding: 24px 26px;
        border: 3px solid var(--dashboard-header-bg, #dbe4f0);
        border-radius: 16px;
        background: var(--dashboard-card-bg, #fff);
        color: var(--text--color-default, #607389);
      }
      .compare-card__header {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .compare-card__title {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
      }
      .compare-card__subtitle {
        margin: 0;
        font-size: 0.85rem;
        color: #5a5f66;
      }
      .compare-card__legend {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        font-size: 0.8rem;
      }
      .compare-card__legend-item {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
      }
      .compare-card__swatch {
        display: inline-block;
        width: 0.7rem;
        height: 0.7rem;
        border-radius: 999px;
      }
      .compare-card__swatch--you {
        background: #1b1d1f;
      }
      .compare-card__swatch--with {
        background: rgba(31, 95, 168, 0.45);
      }
      .compare-card__swatch--without {
        background: rgba(184, 90, 28, 0.45);
      }
      .compare-card__body {
        display: flex;
        flex-direction: column;
        gap: 22px;
      }
      .compare-card__empty {
        margin: 0;
        opacity: 0.7;
      }
    `,
  ],
})
export class HowYouCompareWidgetComponent {
  readonly data = input<CompareWidgetData | null>(null);

  protected readonly visibleMetrics = computed(() => (this.data()?.metrics ?? []).filter(hasAnyTier));

  /** For the (single, card-level) legend: the first visible metric that has this tier, used for its count. */
  private readonly firstWithLoyalty = computed(
    () => this.visibleMetrics().find((m) => m.withLoyalty)?.withLoyalty ?? null
  );
  private readonly firstWithoutLoyalty = computed(
    () => this.visibleMetrics().find((m) => m.withoutLoyalty)?.withoutLoyalty ?? null
  );

  protected readonly hasWithLoyalty = computed(() => this.firstWithLoyalty() != null);
  protected readonly hasWithoutLoyalty = computed(() => this.firstWithoutLoyalty() != null);

  protected trackByKey(_index: number, metric: CompareMetric): string {
    return metric.key;
  }
}
